import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, withCtx as _withCtx, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "text-left flex flex-col" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center flex-1 h-full"
}
const _hoisted_3 = {
  key: 0,
  class: "flex gap-1 items-center absolute top-1 left-[0px]"
}
const _hoisted_4 = { class: "text-xs whitespace-nowrap" }
const _hoisted_5 = {
  key: 0,
  class: "flex-1"
}
const _hoisted_6 = { class: "text-xs flex flex-col gap-2" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = {
  key: 2,
  class: "w-[80px] flex items-center justify-end"
}
const _hoisted_10 = {
  key: 1,
  style: { scrollbarGutter: 'stable' },
  class: "overflow-auto -ml-2 pr-2 flex-1"
}
const _hoisted_11 = {
  key: 2,
  class: "py-2 text-sm truncate h-full flex flex-col items-center justify-center text-center"
}
const _hoisted_12 = {
  key: 3,
  class: "text-sm flex flex-col items-center justify-center gap-1 select-none flex-1 text-center"
}

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { CheckCircleIcon, FunnelIcon } from "@heroicons/vue/24/outline";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/vue/24/solid";
import VueDatePicker from "@vuepic/vue-datepicker";
import { startOfDay } from "date-fns";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { HierarchyTagStore } from "shared/types/HierarchyTag";
import {
  ActualEvent,
  PlanConfig,
  PlannedEvent,
  PlannerItem,
  PlannerItemWithChildren,
} from "shared/types/Plan";
import { ShortenedProcessWithTags } from "shared/types/Process";
import { ProjectDurationSettings } from "shared/types/ProjectDurationSettings";
import { createHierarchyTagContext, tagTypes } from "shared/views/planner/hierarchyTags";
import {
  createPlannerItemsWithChildren,
  filterPlannerItemsByHierarchy,
  filterPlanConfigByLocation,
  filterPlanConfigByPlannerItems,
} from "shared/views/planner/planner";
import LocationFilter from "@/components/process_filters/LocationFilter.vue";
import projectProgressService from "@/services/projectProgressService";
import { PlannerItemProgress } from "@/types/Plan";
import ProjectProgressItem from "@/views/planner/components/ProjectProgressItem.vue";
import exportProgressToExcel from "@/views/planner/progressExcelExport";

const precision = 1;

export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectProgressTable',
  props: {
    planConfig: {},
    processes: {},
    projectDurationSettings: {},
    hierarchyTags: {},
    simplified: { type: Boolean },
    showOnlyActive: { type: Boolean },
    hideCheckIconWhenCompleted: { type: Boolean },
    showEvenWhenCompleted: { type: Boolean },
    initialLocationFilters: {}
  },
  setup(__props: any) {

const props = __props;

const { locale, t } = useI18n();

const selectedDate = ref(startOfDay(new Date()));
const dateChangingTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const locationFilters = ref<string[]>(props.initialLocationFilters || []);

const formatNumber = (number: number) =>
  number.toLocaleString(locale.value, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false,
  });

const formatPercentage = (percentage: number | null) =>
  percentage !== null ? `${formatNumber(percentage * 100)}%` : "-";

const hierarchyTagContext = computed(() => createHierarchyTagContext(props.hierarchyTags));

const tagsByPlannerItemId = computed(() => {
  const { tagsByTypeAndSourceId } = hierarchyTagContext.value;

  return props.planConfig.planner_items.reduce((acc, plannerItem) => {
    acc[plannerItem._id] = tagTypes
      .map((type) => tagsByTypeAndSourceId[`${type}_${plannerItem.source_id}`])
      .filter((tag) => tag) as HierarchyTagStore[];
    return acc;
  }, {} as Record<string, HierarchyTagStore[]>);
});

const isPlannerItemActive = (plannerItem: PlannerItem) => {
  const { tagsByTypeAndSourceId } = hierarchyTagContext.value;
  const plannerItemProgress = plannerItemProgressById.value[plannerItem._id];
  return !!(
    tagsByTypeAndSourceId[`section_${plannerItem.source_id}`] &&
    tagsByTypeAndSourceId[`component_${plannerItem.source_id}`] &&
    plannerItemProgress &&
    plannerItemProgress.total_working_days !== plannerItemProgress.finished_working_days &&
    plannerItemProgress.total_working_days !== 0 &&
    plannerItemProgress.finished_working_days !== 0
  );
};

const filteredPlanConfig = computed(() => {
  const filteredPlan = filterPlanConfigByLocation(
    props.planConfig,
    props.hierarchyTags,
    locationFilters.value,
  );
  const finalPlannerItemsToProcess: PlannerItem[] = props.showOnlyActive
    ? filterPlannerItemsByHierarchy(filteredPlan.planner_items, isPlannerItemActive)
    : filteredPlan.planner_items;
  return filterPlanConfigByPlannerItems(filteredPlan, finalPlannerItemsToProcess);
});

const plannerItemsWithChildren = computed(() =>
  createPlannerItemsWithChildren(filteredPlanConfig.value.planner_items),
);

const childrenCountByLevel = computed(() => {
  const result: Record<number, number> = {};
  let plannerItems: PlannerItemWithChildren[] = plannerItemsWithChildren.value;
  let level = 0;
  while (plannerItems.length > 0) {
    const allChildren: PlannerItemWithChildren[] = [];
    for (const plannerItem of plannerItems) {
      for (const child of plannerItem.children || []) {
        allChildren.push(child);
      }
    }
    result[level] = allChildren.length;
    level += 1;
    plannerItems = allChildren;
  }
  return result;
});

const plannerItemProgresses = computed(() =>
  projectProgressService.calculateProjectProgressItems(
    // It's a bit tricky here.
    // When props.showOnlyActive is true, filteredPlanConfig USES the value of plannerItemProgresses to filter.
    // This means, here we can't use filteredPlanConfig for that case, because that would create a circular dependency.
    props.showOnlyActive ? props.planConfig : filteredPlanConfig.value,
    props.processes,
    props.projectDurationSettings,
    props.hierarchyTags,
    selectedDate.value,
  ),
);

const plannerItemProgressById = computed(() =>
  plannerItemProgresses.value.reduce((acc, plannerItemProgress) => {
    acc[plannerItemProgress._id] = plannerItemProgress;
    return acc;
  }, {} as Record<string, PlannerItemProgress>),
);

const totalProgress = computed(() =>
  projectProgressService.calculateProjectProgress(plannerItemProgresses.value),
);

const totalPercentage = computed(() =>
  projectProgressService.calculatePercentage(
    totalProgress.value.finishedWorkingDays,
    totalProgress.value.totalWorkingDays,
  ),
);

const actualEventsBySourceId = computed(() =>
  props.planConfig.actual_events.reduce((acc, actualEvent) => {
    acc[actualEvent.source_id] = actualEvent;
    return acc;
  }, {} as Record<string, ActualEvent | undefined>),
);

const plannedEventsByPlannerId = computed(() =>
  props.planConfig.planned_events.reduce((acc, plannedEvent) => {
    acc[plannedEvent.planner_item_id] = plannedEvent;
    return acc;
  }, {} as Record<string, PlannedEvent>),
);

const isLoading = computed(() => !!dateChangingTimer.value);

const processesBySourceId = computed(() => {
  const sortedProcesses = props.processes.slice();
  sortedProcesses.sort((a, b) => a.start_time.getTime() - b.start_time.getTime());
  return sortedProcesses.reduce((acc, process) => {
    const key = process.planner_item_mapping?.source_id;
    if (key) {
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(process);
    }
    return acc;
  }, {} as Record<string, ShortenedProcessWithTags[]>);
});

const plannedProgress = computed(() => {
  const planForPlannedProgress = projectProgressService.calculatePlanForPlannedProgress(
    filteredPlanConfig.value,
  );
  const processes = undefined;
  return projectProgressService.calculateProjectProgressPercentage(
    planForPlannedProgress,
    processes,
    props.projectDurationSettings,
    props.hierarchyTags,
    selectedDate.value,
  );
});

const isCompleted = computed(
  () =>
    plannerItemProgresses.value.length > 0 &&
    plannerItemProgresses.value.every(
      (plannerItemProgress) =>
        plannerItemProgress.finished_working_days === plannerItemProgress.total_working_days &&
        plannerItemProgress.total_working_days !== 0,
    ),
);

const hasLocationFilter = computed(() => locationFilters.value.length > 0);

const handleSelectedDateChange = () => {
  if (dateChangingTimer.value) {
    clearTimeout(dateChangingTimer.value);
  }
  dateChangingTimer.value = setTimeout(() => {
    dateChangingTimer.value = null;
  }, 500);
};

const handleLocationFilterChange = (payload: string[]) => {
  locationFilters.value = payload;
};

const handleExportExportClick = () => {
  exportProgressToExcel(
    plannerItemsWithChildren.value,
    plannerItemProgresses.value,
    props.hierarchyTags,
    props.planConfig,
    props.projectDurationSettings,
  );
};

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(LoadingSpinner)
        ]))
      : _createCommentVNode("", true),
    (
        !isLoading.value &&
        (plannerItemsWithChildren.value.length > 0 || hasLocationFilter.value) &&
        (!isCompleted.value || _ctx.showEvenWhenCompleted)
      )
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["relative h-full flex flex-col", _ctx.simplified ? '' : 'pt-3'])
        }, [
          (!_ctx.simplified)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("analytics.reports.query_value_progress.progress_for_date")) + ": ", 1),
                _createVNode(_unref(VueDatePicker), {
                  "input-class-name": "!text-xs",
                  modelValue: selectedDate.value,
                  "onUpdate:modelValue": [
                    _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
                    handleSelectedDateChange
                  ],
                  "enable-time-picker": false,
                  format: "dd.MM.yyyy",
                  locale: _unref(locale),
                  "auto-apply": "",
                  "reverse-years": "",
                  class: "w-[150px]",
                  clearable: false
                }, null, 8, ["modelValue", "locale"]),
                _createVNode(LocationFilter, {
                  class: "ml-3 text-left",
                  style: { lineHeight: '0' },
                  hierarchyTags: _ctx.hierarchyTags,
                  selected: locationFilters.value,
                  popoverClass: "leading-normal",
                  onChange: handleLocationFilterChange
                }, {
                  default: _withCtx(() => [
                    (!hasLocationFilter.value)
                      ? (_openBlock(), _createBlock(_unref(FunnelIcon), {
                          key: 0,
                          class: "h-5 w-5 text-gray-500"
                        }))
                      : (_openBlock(), _createBlock(_unref(FunnelIconSolid), {
                          key: 1,
                          class: "h-5 w-5 text-yellow-400"
                        }))
                  ]),
                  _: 1
                }, 8, ["hierarchyTags", "selected"]),
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "fa-solid fa-file-excel",
                  class: "ml-3 w-5 h-5 text-green cursor-pointer",
                  onClick: handleExportExportClick
                })
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass(["flex gap-2 text-xs px-4 overflow-auto", _ctx.simplified ? '' : 'mb-1 pb-3 border-b']),
            style: { scrollbarGutter: 'stable' }
          }, [
            (!_ctx.simplified)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5))
              : _createCommentVNode("", true),
            (!_ctx.simplified)
              ? (_openBlock(), _createBlock(OaiTooltip, {
                  key: 1,
                  cls: "w-[80px] flex items-center justify-center whitespace-nowrap"
                }, {
                  tooltip: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", null, _toDisplayString(formatNumber(totalProgress.value.finishedWorkingDays)) + " / " + _toDisplayString(formatNumber(totalProgress.value.totalWorkingDays)) + " " + _toDisplayString(_unref(t)("analytics.planner.working_days_label")) + " (" + _toDisplayString(_unref(t)("analytics.reports.query_value_progress.finished_working_hours_parent_tooltip")) + ") ", 1),
                      (plannedProgress.value !== null)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_unref(t)("analytics.planner.actual_progress")) + ": " + _toDisplayString(formatPercentage(totalPercentage.value)), 1))
                        : _createCommentVNode("", true),
                      (plannedProgress.value !== null)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)("analytics.planner.planned_progress")) + ": " + _toDisplayString(formatPercentage(plannedProgress.value)), 1))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(formatPercentage(totalPercentage.value)) + _toDisplayString(plannedProgress.value !== null ? `/ ${formatPercentage(plannedProgress.value)}` : "") + " ", 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (!_ctx.simplified)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(OaiTooltip, { cls: "cursor-pointer" }, {
                    tooltip: _withCtx(() => [
                      _createElementVNode("div", null, _toDisplayString(_unref(t)("analytics.reports.query_value_progress.weight_tooltip")), 1)
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("analytics.reports.query_value_progress.weight")) + " ", 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ], 2),
          (_ctx.projectDurationSettings)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(plannerItemsWithChildren.value, (plannerItem) => {
                  return (_openBlock(), _createBlock(ProjectProgressItem, {
                    key: plannerItem._id,
                    plannerItem: plannerItem,
                    plannerItemProgressById: plannerItemProgressById.value,
                    hierarchyTags: _ctx.hierarchyTags,
                    actualEventsBySourceId: actualEventsBySourceId.value,
                    plannedEventsByPlannerId: plannedEventsByPlannerId.value,
                    simplified: _ctx.simplified,
                    processesBySourceId: processesBySourceId.value,
                    level: 0,
                    parentDefaultOpen: true,
                    childrenCountByLevel: childrenCountByLevel.value,
                    tagsByPlannerItemId: tagsByPlannerItemId.value,
                    projectDurationSettings: _ctx.projectDurationSettings,
                    totalProgress: totalProgress.value
                  }, null, 8, ["plannerItem", "plannerItemProgressById", "hierarchyTags", "actualEventsBySourceId", "plannedEventsByPlannerId", "simplified", "processesBySourceId", "childrenCountByLevel", "tagsByPlannerItemId", "projectDurationSettings", "totalProgress"]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    (
        !isLoading.value && plannerItemsWithChildren.value.length === 0 && !hasLocationFilter.value && !isCompleted.value
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(t)("dashboard.project.planner_card.no_current_procedures")), 1))
      : _createCommentVNode("", true),
    (!isLoading.value && isCompleted.value && !_ctx.showEvenWhenCompleted)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          (!_ctx.hideCheckIconWhenCompleted)
            ? (_openBlock(), _createBlock(_unref(CheckCircleIcon), {
                key: 0,
                class: "h-16 w-16 text-green-400"
              }))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t("analytics.planner.project_completed_message")), 1),
          _createVNode(_component_RouterLink, {
            to: {
          name: 'PlannerV2',
          params: { customer_name: _ctx.currentCustomerName, site_id: _ctx.currentSiteId },
        },
            class: "text-gray-600 underline"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("analytics.planner.to_planner_link")), 1)
            ]),
            _: 1
          }, 8, ["to"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})